<script setup>
import { computed, shallowRef, watch } from 'vue';
import { useStore } from 'vuex';
import StudentDisciplinaryDialog from '@/components/Dialogs/Forms/StudentDisciplinaryDialog.vue';
import ReportsMenu from '@/components/dashboard/ReportsMenu.vue';
import UiBadge from '@/components/ui/UiBadge.vue';
import UiBarListCard from '@/components/ui/UiBarListCard.vue';
import UiCard from '@/components/ui/UiCard.vue';
import UiEmptyState from '@/components/ui/UiEmptyState.vue';
import UiPageHeader from '@/components/ui/UiPageHeader.vue';
import UiPerson from '@/components/ui/UiPerson.vue';
import UiStatCard from '@/components/ui/UiStatCard.vue';
import UiTableCard from '@/components/ui/UiTableCard.vue';
import { apiRequest, errorMessage } from '@/api/http';
import { useOpenPdf } from '@/composables/useOpenPdf';
import { MONITORING_STATUSES, complianceStatus } from '@/constants/reports';
import { formatDate } from '@/utils/format';

// Prefect dashboard: incident totals, incidents by status, recent
// incidents, At-Risk/LARDO referrals, repeat offenders and the behavior
// split.
const STATUS_VARIANT = {
  Pending: 'amber',
  Resolved: 'green',
  Adviser: 'red',
  Unresolved: 'red',
  'Parent Meeting': 'violet',
  Counseling: 'blue',
};
const BEHAVIOR_COLOR = { green: '#10b981', orange: '#f59e0b', red: '#ef4444', grey: '#94a3b8' };

const store = useStore();
const year = computed(() => store.getters.getFilterSelected);

const data = shallowRef({});
const loading = shallowRef(false);
const error = shallowRef('');

async function load() {
  if (!year.value) return;
  loading.value = true;
  error.value = '';
  try {
    data.value = (await apiRequest('get', `/enroll-student/getPrefectDashboardData/${year.value}`)) || {};
  } catch (e) {
    error.value = errorMessage(e, 'Could not load the dashboard.');
  } finally {
    loading.value = false;
  }
}
watch(year, load, { immediate: true });

const stats = computed(() => data.value.stats ?? []);
const incidents = computed(() => (data.value.incidents ?? []).map((row, i) => ({ ...row, key: i })));
const referrals = computed(() => data.value.academicReferrals ?? []);
const offenders = computed(() => data.value.topOffenders ?? []);
const behavior = computed(() => data.value.behaviorSummary ?? []);

const statusRows = computed(() => {
  const counts = {};
  incidents.value.forEach((i) => (counts[i.status] = (counts[i.status] || 0) + 1));
  return Object.entries(counts).map(([label, value]) => ({ label, value }));
});
const behaviorRows = computed(() => behavior.value.map((b) => ({ label: b.label, value: Number(b.value) || 0, count: Number(b.count) || 0, color: b.color })));

const headers = [
  { title: 'Student', key: 'student' },
  { title: 'Violation', key: 'violation' },
  { title: 'Date', key: 'date' },
  { title: 'Status', key: 'status' },
  { title: 'Compliance', key: 'compliance' },
];

// The cases received this school year as a PDF (the server limits it to
// the Prefect's grades and prints them as "Prepared by").
const reports = computed(() => [
  {
    title: 'Discipline cases',
    options: [
      { title: 'All cases', path: `/pdf-generator/prefectCaseList/${year.value}` },
      { title: 'Open cases', path: `/pdf-generator/prefectCaseList/${year.value}?status=open` },
      { title: 'Resolved cases', path: `/pdf-generator/prefectCaseList/${year.value}?status=resolved` },
    ],
  },
]);
const referralHeaders = [
  { title: 'Student', key: 'student' },
  { title: 'Alert', key: 'monitoring' },
  { title: 'Referred on', key: 'date' },
  { title: 'Status', key: 'status' },
];
const openPdf = useOpenPdf();

// Record a case no teacher reported; it's then on Discipline Records.
const recordOpen = shallowRef(false);
</script>

<template>
  <div class="page-stack">
    <UiPageHeader title="Dashboard" subtitle="Student discipline for the selected school year.">
      <template #actions>
        <v-btn variant="text" icon="mdi-refresh" size="small" :loading="loading" aria-label="Refresh" @click="load" />
        <ReportsMenu :reports="reports" @open="(report) => openPdf(report.path)" />
        <v-btn color="primary" prepend-icon="mdi-plus" @click="recordOpen = true">Add record</v-btn>
      </template>
    </UiPageHeader>

    <v-alert v-if="error" type="error" variant="tonal" density="compact">{{ error }}</v-alert>

    <section class="stat-grid" aria-label="Totals">
      <UiStatCard
        v-for="card in stats"
        :key="card.title"
        :title="card.title"
        :icon="card.icon || 'mdi-chart-box-outline'"
        :value="card.value"
        :loading="loading"
      />
    </section>

    <section class="chart-grid">
      <UiBarListCard
        title="Incidents by status"
        description="How many incidents sit in each stage."
        :rows="statusRows"
        :series="[{ key: 'value', label: 'Incidents', color: 'var(--brand)' }]"
        :loading="loading"
        empty-text="Reported incidents will be charted here."
      />
      <UiCard title="Behavior summary" description="Share of your cases at each severity, as you classified them.">
        <div v-if="behaviorRows.length" class="levels">
          <div v-for="row in behaviorRows" :key="row.label" class="levels__row">
            <div class="levels__top">
              <span>{{ row.label }}</span>
              <span class="levels__value">{{ row.count }} {{ row.count === 1 ? 'case' : 'cases' }} · {{ row.value }}%</span>
            </div>
            <div class="levels__track">
              <span :style="{ width: `${Math.min(100, row.value)}%`, background: BEHAVIOR_COLOR[row.color] || 'var(--brand)' }" />
            </div>
          </div>
        </div>
        <p v-else class="text-body-2 text-medium-emphasis mb-0">Behavior levels appear once records exist.</p>
      </UiCard>
    </section>

    <section class="incidents-grid">
      <UiTableCard
        title="Recent incidents"
        :description="`${incidents.length} ${incidents.length === 1 ? 'incident' : 'incidents'}`"
        :headers="headers"
        :items="incidents"
        item-value="key"
        :loading="loading"
        search-placeholder="Search student or violation"
        empty-text="Nothing has been reported."
      >
        <template #[`item.student`]="{ item }">
          <UiPerson :name="item.student" />
        </template>
        <template #[`item.date`]="{ item }">{{ formatDate(item.date) || item.date }}</template>
        <template #[`item.status`]="{ item }">
          <UiBadge :variant="STATUS_VARIANT[item.status] || 'secondary'">{{ item.status }}</UiBadge>
        </template>
        <template #[`item.compliance`]="{ item }">
          <UiBadge v-if="complianceStatus(item.compliance)" :variant="complianceStatus(item.compliance).variant">
            {{ complianceStatus(item.compliance).title }}
          </UiBadge>
          <span v-else class="text-medium-emphasis">—</span>
        </template>
      </UiTableCard>

      <UiCard title="Repeat offenders" description="Students ranked by number of cases.">
        <ol v-if="offenders.length" class="ranking">
          <li v-for="(student, i) in offenders" :key="student.name + i" class="ranking__row">
            <span class="ranking__place">{{ i + 1 }}</span>
            <span class="ranking__name">{{ student.name }}</span>
            <UiBadge variant="outline">{{ student.cases }} {{ student.cases === 1 ? 'case' : 'cases' }}</UiBadge>
          </li>
        </ol>
        <UiEmptyState v-else icon="mdi-account-check-outline" title="No repeat cases" description="Students with several cases will be listed here." />
      </UiCard>
    </section>

    <UiTableCard
      title="At-Risk and LARDO referrals"
      description="Students the class advisers referred to you through an academic concern."
      :headers="referralHeaders"
      :items="referrals"
      item-value="id"
      :loading="loading"
      search-placeholder="Search student"
      empty-text="No adviser has referred an At-Risk or LARDO student."
    >
      <template #[`item.student`]="{ item }">
        <UiPerson :name="item.student" :subtitle="item.detail" />
      </template>
      <template #[`item.monitoring`]="{ item }">
        <UiBadge v-if="MONITORING_STATUSES[item.monitoring]" :variant="MONITORING_STATUSES[item.monitoring].variant">
          {{ MONITORING_STATUSES[item.monitoring].name }}
        </UiBadge>
        <span v-else class="text-medium-emphasis">—</span>
      </template>
      <template #[`item.date`]="{ item }">{{ formatDate(item.date) || item.date }}</template>
      <template #[`item.status`]="{ item }">
        <UiBadge :variant="STATUS_VARIANT[item.status] || 'secondary'">{{ item.status }}</UiBadge>
      </template>
    </UiTableCard>

    <StudentDisciplinaryDialog v-model="recordOpen" mode="prefect" @saved="load" />
  </div>
</template>

<style scoped>
.incidents-grid {
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(260px, 1fr);
  gap: 16px;
  align-items: start;
}
@media (max-width: 960px) {
  .incidents-grid {
    grid-template-columns: 1fr;
  }
}
.levels {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.levels__top {
  display: flex;
  justify-content: space-between;
  margin-bottom: 6px;
  font-size: 14px;
}
.levels__value {
  font-variant-numeric: tabular-nums;
  color: var(--muted-foreground);
}
.levels__track {
  height: 6px;
  border-radius: 9999px;
  background-color: var(--accent);
  overflow: hidden;
}
.levels__track > span {
  display: block;
  height: 100%;
  border-radius: inherit;
}
.ranking {
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  list-style: none;
}
.ranking__row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid var(--border);
}
.ranking__row:last-child {
  border-bottom: 0;
}
.ranking__place {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  background-color: var(--accent);
  font-size: 12px;
  font-weight: 600;
}
.ranking__name {
  flex: 1;
  min-width: 0;
  font-size: 14px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
